/**
 * Tenant-authorable token key ceiling — Site Studio write boundary + Portal adapter SSOT.
 *
 * Governs which `SiteDesignManifest.tokens.{primitive,semantic,component}` keys a tenant
 * may author at all (W2 write/resolve boundary), and which subset of those keys may ever
 * reach the Portal (tenant-owned relationship workspace) surface (W1 adapter boundary).
 *
 * Operational semantic truth (status/severity/focus) is never tenant-authorable and never
 * portal-adapted — it is owned by Inflomatrix core tokens (`src/styles/tokens/semantic.css`).
 *
 * @see docs/product/foundations/FOUR_LAYER_PRODUCT_SURFACE_CANON_V0.json
 * @see docs/inflomatrix-constitution/ui-architecture/BRAND_PLANE_BRIDGE.md
 */

/** Primitive tier — raw brand values (color swatches, typeface families). */
export const TENANT_AUTHORABLE_PRIMITIVE_KEYS = [
  '--color-brand-primary',
  '--color-brand-secondary',
  '--color-brand-surface',
  '--color-brand-on-surface',
  '--color-brand-accent',
  '--font-display',
  '--font-body',
] as const;

/** Semantic tier — brand role bindings (must resolve via `var()` onto primitives). */
export const TENANT_AUTHORABLE_SEMANTIC_BRAND_KEYS = [
  '--color-accent',
  '--color-surface',
  '--color-on-surface',
  '--color-primary',
  '--section-gap-sm',
  '--section-gap-md',
  '--section-gap-lg',
  '--section-stack-gap',
  '--font-display',
  '--font-body',
  '--button-bg',
  '--card-radius',
  '--radius-md',
  '--radius-lg',
] as const;

/** Component tier — presentation-only leaf tokens (buttons, cards, section typography scale). */
export const TENANT_AUTHORABLE_COMPONENT_PRESENTATION_KEYS = [
  '--button-bg',
  '--button-radius',
  '--button-color',
  '--card-bg',
  '--card-radius',
  '--card-padding',
  '--section-title-size',
  '--section-subtitle-size',
  '--section-eyebrow-size',
  '--radius-md',
  '--radius-lg',
] as const;

/** Non-manifest brand metadata carried on `theme.cssVars` (logo / display name). */
export const TENANT_AUTHORABLE_BRAND_METADATA_KEYS = [
  '--brand-logo-url',
  '--logo-url',
  '--brand-display-name',
  '--brand-tenant-name',
  '--tenant-display-name',
] as const;

/** Full write-boundary ceiling — union of every tenant-authorable key across tiers. */
export const TENANT_AUTHORABLE_TOKEN_KEYS = [
  ...TENANT_AUTHORABLE_PRIMITIVE_KEYS,
  ...TENANT_AUTHORABLE_SEMANTIC_BRAND_KEYS,
  ...TENANT_AUTHORABLE_COMPONENT_PRESENTATION_KEYS,
  ...TENANT_AUTHORABLE_BRAND_METADATA_KEYS,
] as const;

const TENANT_AUTHORABLE_TOKEN_KEY_SET = new Set<string>(TENANT_AUTHORABLE_TOKEN_KEYS);

export type TenantAuthorableTokenKey = (typeof TENANT_AUTHORABLE_TOKEN_KEYS)[number];

/**
 * Portal runtime allowlist — subset of {@link TENANT_AUTHORABLE_TOKEN_KEYS} permitted to
 * influence the Portal adapter output. Surface/on-surface primitives are intentionally
 * excluded here: Portal derives its shell surface/text aliases from the Inflomatrix
 * operational default unless a future Primary explicitly extends this allowlist.
 */
export const PORTAL_ADAPTER_ALLOWLIST_KEYS = [
  '--color-brand-primary',
  '--color-brand-secondary',
  '--color-brand-accent',
  '--brand-logo-url',
  '--logo-url',
  '--brand-display-name',
  '--font-body',
  '--radius-md',
] as const;

export type PortalAdapterAllowlistKey = (typeof PORTAL_ADAPTER_ALLOWLIST_KEYS)[number];

/**
 * Portal denylist — must never appear in Portal-adapted output, regardless of source.
 * Operational semantic truth (status/severity/focus) + global ERP surface/text tokens +
 * the Public-only display typeface.
 */
export const PORTAL_ADAPTER_DENYLIST_KEYS = [
  '--error-color',
  '--warning-color',
  '--success-color',
  '--info-color',
  '--state-focus-ring',
  '--state-hover-overlay',
  '--state-active-overlay',
  '--approval-status-color',
  '--evidence-status-color',
  '--danger-color',
  '--color-error-500',
  '--color-warning-500',
  '--color-success-500',
  '--badge-success-bg',
  '--badge-warning-bg',
  '--badge-error-bg',
  '--badge-info-bg',
  '--surface-glass-02',
  '--surface-glass-03',
  '--surface-glass-04',
  '--surface-glass-06',
  '--surface-glass-10',
  '--surface-canvas',
  '--surface-subtle',
  '--color-text',
  '--color-text-primary',
  '--color-text-soft',
  '--color-text-muted',
  '--color-bg-primary',
  '--color-bg-secondary',
  '--color-bg-tertiary',
  '--color-aqua',
  '--layout-bg',
  '--font-display',
] as const;

export type PortalAdapterDenylistKey = (typeof PORTAL_ADAPTER_DENYLIST_KEYS)[number];

const PORTAL_ADAPTER_DENYLIST_SET = new Set<string>(PORTAL_ADAPTER_DENYLIST_KEYS);

/** Write/resolve boundary predicate — is this key ever tenant-authorable? */
export function isTenantAuthorableTokenKey(key: string): boolean {
  return TENANT_AUTHORABLE_TOKEN_KEY_SET.has(key);
}

/** Portal boundary predicate — is this key explicitly denylisted from Portal output? */
export function isPortalDenylistedTokenKey(key: string): boolean {
  return PORTAL_ADAPTER_DENYLIST_SET.has(key);
}

export interface StripNonAuthorableTokenKeysResult {
  kept: Record<string, string>;
  stripped: string[];
}

/**
 * Fail-closed strip — drop any key outside the tenant-authorable write-boundary ceiling.
 * Used both at manifest→cssVars resolve time and defensively inside surface adapters.
 */
export function stripNonAuthorableTokenKeys(
  cssVars: Record<string, string>
): StripNonAuthorableTokenKeysResult {
  const kept: Record<string, string> = {};
  const stripped: string[] = [];
  for (const [key, value] of Object.entries(cssVars)) {
    if (isTenantAuthorableTokenKey(key)) {
      kept[key] = value;
    } else {
      stripped.push(key);
    }
  }
  return { kept, stripped };
}
